import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  HostBinding,
  inject,
  input,
  Input,
  Output,
} from '@angular/core';
import {
  EstimateItemModel,
  SubscriptionEstimatesModel,
} from '@app/+authenticated/+reports/shared/subscriptions/subscription.model';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { SubscriptionCurrencyPipe } from '@app/pipes/subscription-currency.pipe';
import { VatResponseStatus } from '@app/shared/billing/billing.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { mgcIconCheckCircleRegular } from '@sb/mgc-icons/mgcIcon-check_circle_regular.icon';
import { DividerDirective } from '@sb/ui/divider';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import { clsx } from 'clsx';

import { VatChanges } from '../../forms/billing-details/billing-details-form.component';
import { OrderSummaryRowComponent, OrderSummaryRowItemModel } from '../order-summary-row/order-summary-row.component';
import {
  ESTIMATE_ITEM_DESCRIPTION_TYPE_MAP,
  ESTIMATE_ITEM_NOTE_MAP,
  ESTIMATE_ITEM_PRIORITY,
  EstimateItemType,
  getEstimateItemType,
} from './order-summary-content.helper';

interface OrderSummaryLineItem {
  id: string;
  type: EstimateItemType;
  item: OrderSummaryRowItemModel;
  extraItem?: OrderSummaryRowItemModel;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'order-summary-content',
  templateUrl: './order-summary-content.component.html',
  imports: [OrderSummaryRowComponent, TranslatePipe, LocaleDatePipe, IconComponent, DividerDirective],
  providers: [SubscriptionCurrencyPipe],
})
export class OrderSummaryContentComponent implements AfterViewInit {
  private subscriptionCurrencyPipe = inject(SubscriptionCurrencyPipe);
  private translate = inject(TranslateService);

  @HostBinding('class')
  hostClasses = clsx('flex flex-col gap-4');

  estimates = input.required<SubscriptionEstimatesModel>();

  yearlyBilling = input<boolean>(false);

  @Input()
  hasIntegrationPlus: boolean;

  countryCode = input<string>('');

  @Input()
  isEntitled = false;

  @Output()
  initialized = new EventEmitter<void>();

  orderSummaryLineItems = computed<OrderSummaryLineItem[]>(() => {
    const lineItems = this.estimates()?.line_items ?? [];
    const showSubscriptionDiscount = this.showSubscriptionDiscount();
    return (
      lineItems
        .map((lineItem) => this.getOrderSummaryLineItem(lineItem, showSubscriptionDiscount))
        // we do not want to show items that are not known since this can be changed directly in chargebee
        .filter((lineItem) => lineItem.type !== 'unknown')
        .sort((a, b) => ESTIMATE_ITEM_PRIORITY.indexOf(a.type) - ESTIMATE_ITEM_PRIORITY.indexOf(b.type))
    );
  });

  vatInfo = input<VatChanges>();

  vatNote = computed<string>(() => {
    const parts = [this.countryCode(), `${this.vatPercentage()}%`];

    if (this.vatInfo()?.status === VatResponseStatus.UNDETERMINED) {
      parts.push(this.translate.instant('(could not validate VAT number)'));
    }

    return parts.filter(Boolean).join(' ');
  });

  vatPercentage = computed<number>(() => {
    const taxDescription = this.estimates()?.taxes?.[0]?.description ?? null;
    if (taxDescription) {
      return parseInt(taxDescription.substring(taxDescription.indexOf('@') + 1));
    }
    return 0;
  });

  /**
   * Determines if we should show a subscription-wide discount row
   * Backend tells us when all items have the same discount applied at subscription level
   */
  showSubscriptionDiscount = computed<boolean>(() => {
    const hasSubscriptionDiscount = this.estimates()?.has_subscription_discount ?? false;
    const hasItemDiscount = this.estimates()?.has_item_discount ?? false;
    return hasSubscriptionDiscount && !hasItemDiscount;
  });

  /**
   * Get the discount percentage for subscription-wide discount
   * Calculate from total_discount and original subtotal before discount
   */
  subscriptionDiscountPercentage = computed<number>(() => {
    const showSubscriptionDiscount = this.showSubscriptionDiscount();
    const totalDiscount = this.estimates()?.total_discount ?? 0;
    const subtotalBeforeDiscount = this.estimates()?.subtotal_before_discount ?? 0;
    if (!showSubscriptionDiscount || !totalDiscount || !subtotalBeforeDiscount) {
      return 0;
    }
    return Math.round((totalDiscount / subtotalBeforeDiscount) * 100);
  });

  hasAnyDiscount = computed<boolean>(() => (this.estimates()?.total_discount ?? 0) > 0);

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconCheckCircleRegular]);
  }

  ngAfterViewInit(): void {
    this.initialized.emit();
  }

  private getOrderSummaryLineItem(item: EstimateItemModel, showSubscriptionDiscount: boolean): OrderSummaryLineItem {
    const type = getEstimateItemType(item);
    return {
      id: item.entity_id,
      type,
      item: {
        description: this.getEstimateItemDescription(type, item),
        amount: item.amount / 100,
        note: this.getEstimateItemNote(item),
      },
      extraItem: showSubscriptionDiscount ? null : this.getEstimateDiscountItem(item),
    };
  }

  private getEstimateItemDescription(type: EstimateItemType, item: EstimateItemModel): string {
    const descriptionFromMap = ESTIMATE_ITEM_DESCRIPTION_TYPE_MAP[type];
    return descriptionFromMap ? this.translate.instant(descriptionFromMap) : item.description;
  }

  private getEstimateDiscountItem(item: EstimateItemModel): OrderSummaryRowItemModel | null {
    if (!item?.discount_amount) {
      return null;
    }
    return {
      description: this.translate.instant('Discount'),
      amount: (item?.discount_amount * -1) / 100,
    };
  }

  getEstimateItemNote(item: EstimateItemModel): string {
    const isYearly = this.yearlyBilling();
    const note = ESTIMATE_ITEM_NOTE_MAP[isYearly ? 'yearly' : 'monthly'][item.pricing_model];
    if (!note) return '';
    const unitAmount = isYearly ? item.unit_amount / 100 / 12 : item.unit_amount / 100;
    return this.translate.instant(note, {
      price: this.subscriptionCurrencyPipe.transform(unitAmount),
      quantity: item.quantity,
    });
  }
}
